import { useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  DateField,
  NumberField,
  useTheme,
  FunctionField,
  RecordContextProvider,
  ChipField,
  useTranslate,
  WrapperField,
} from "react-admin";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import cashedReturnedCheckFilterList from "../../components/cashedReturnedChecks/filter/cashedReturnedCheckFilterList";
import CashedReturnedCheckExpandList from "../../components/cashedReturnedChecks/expand/CashedReturnedCheckExpandList";
import CashedReturnedCheckFooterList from "../../components/cashedReturnedChecks/footer/CashedReturnedCheckFooterList";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const CashedReturnedChecksList = () => {
  const { permissions, filterDefaultValues } = useCompanyScopedListFilters({
    start_date: firstDate,
    end_date: lastDate,
    approved: 1,
  });
  const [theme] = useTheme("light");
  const t = useTranslate();

  const list = useMemo(
    () => (
      <ManualReportList
        title="mine.label.cashed_returned_check"
        empty={false}
        actions={<DownloadAction />}
        filters={cashedReturnedCheckFilterList(permissions)}
        queryOptions={{
          meta: {
            sort: ["check_cashings.type DESC", "check_cashings.code ASC"],
          },
        }} // overide default sort
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date"]}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<CashedReturnedCheckFooterList />} />}
          bulkActionButtons={false}
          expand={<CashedReturnedCheckExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {permissions && permissions.update_company ? (
            <TextField label="Company" source="company.alias" />
          ) : null}
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <WrapperField label="mine.label.customer">
            <TextField source="customer_debt_payment.customer.code" />
            {" - "}
            <TextField source="customer_debt_payment.customer.name" />
          </WrapperField>
          <TextField
            label="mine.label.check_number"
            source="customer_debt_payment.giro"
          />
          <TextField
            label="mine.label.bank_name"
            source="customer_debt_payment.bank_name"
          />
          <WrapperField label="COA" sortBy="chart_of_accounts.code">
            <TextField source="chart_of_account.code" />
            {" - "}
            <TextField source="chart_of_account.name" />
          </WrapperField>
          <FunctionField
            textAlign="center"
            label="mine.label.type"
            render={(record) => {
              let rec = {};
              if (record.type === 1) {
                rec.type = t("mine.label.cashing");
                rec.color = "primary";
              } else if (record.type === 2) {
                rec.type = t("mine.label.cancel");
                rec.color = "error";
              } else if (record.type === 3) {
                rec.type = t("mine.label.rejected");
                rec.color = "error";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <NumberField label="Total" source="customer_debt_payment.total" />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions, filterDefaultValues]
  );
  return list;
};

export default CashedReturnedChecksList;
